import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type SxProps, type Theme } from '@mui/material';
import clsx from 'clsx';
import { mergeSx } from 'merge-sx';

import IndicatorChip, { IndicatorChipVariant } from '@/components/statuses/IndicatorChip';

export interface StatusChipIndicatorProps {
  value: string | number | null;
  onClick?: <EventType>(event: EventType) => void;
  isSelected?: boolean;
}

const createStatusChipIndicator = ({
  prefix,
  options,
  defaultSx,
  translationNamespace,
  normalizeStatus
}: {
  prefix: string;
  options: IndicatorChipVariant[];
  defaultSx: SxProps<Theme>;
  translationNamespace?: string;
  normalizeStatus?: (rawStatus: string | number | null) => string | number | null;
}) => {
  const StatusChipIndicator: FC<StatusChipIndicatorProps> = ({ value: currentValue, onClick, isSelected }) => {
    const { t } = useTranslation(translationNamespace);
    const normalizedValue = useMemo(
      () => (normalizeStatus ? normalizeStatus?.(currentValue) : currentValue),
      [currentValue]
    );
    const chipVariant = useMemo(() => options.find(({ value }) => value === normalizedValue), [normalizedValue]);

    if (!chipVariant) return null;

    const { label, Icon, sx, value } = chipVariant;
    const stringifiedValue = (value || '').toString();

    return (
      <IndicatorChip
        label={typeof label === 'function' ? label(t) : label}
        icon={Icon}
        sx={mergeSx<Theme>(defaultSx, sx)}
        onClick={onClick}
        isSelected={isSelected}
        className={clsx(
          `StatusChipIndicator-${prefix}`,
          `StatusChipIndicator-${prefix}-${stringifiedValue.toLowerCase()}`
        )}
      />
    );
  };

  return StatusChipIndicator;
};

export default createStatusChipIndicator;
